import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Airflow } from '@aviata/platform-types';

import type { NormalizedMIProduct, SavePassengersResponse } from '../types';

export const useMedicalInsuranceStore = defineStore('medicalInsurance', () => {
  const passengersSaveResponse = ref<SavePassengersResponse[]>([]);
  const medicalInsuranceProducts = ref<NormalizedMIProduct[]>([]);

  const passengersWithMedicalInsurance = computed(() =>
    passengersSaveResponse.value
      ?.map(passenger => {
        const matchedProduct = medicalInsuranceProducts.value.find(
          product => product.passengerIdx === passenger.idx,
        );

        if (!matchedProduct) return null;

        return {
          ...passenger,
          productId: matchedProduct.id,
          productPriceAmount: matchedProduct?.price?.amount || 0,
          policyPayout: matchedProduct.$meta.special?.policy_payout,
          currentPriceId: matchedProduct.$meta.special?.current_price_id,
        };
      })
      .filter(Boolean),
  );

  function setPassengersSaveResponse(newPassengers: SavePassengersResponse[]) {
    passengersSaveResponse.value = newPassengers;
  }

  function setMedicalInsuranceProducts(products: NormalizedMIProduct[]) {
    medicalInsuranceProducts.value = products;
  }

  function updateMedicalInsuranceProducts(
    updated: Airflow.Products.MedicalInsurance[],
  ) {
    const current = [...medicalInsuranceProducts.value];

    const normalized = updated.map(product => ({
      ...product,
      passengerIdx: product.passenger_idx,
    }));

    normalized.forEach(product => {
      const index = current.findIndex(p => p.id === product.id);
      if (index !== -1) {
        current[index] = product;
      } else {
        current.push(product);
      }
    });

    medicalInsuranceProducts.value = current;
  }

  return {
    passengersSaveResponse,
    medicalInsuranceProducts,
    passengersWithMedicalInsurance,
    setPassengersSaveResponse,
    setMedicalInsuranceProducts,
    updateMedicalInsuranceProducts,
  };
});
